import { FC, useEffect, useMemo, useState } from "react";
import { useHistory, Link as RouterLink } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Stack,
  Alert,
  Divider,
  Button,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isEmpty } from "lodash";
import { format } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useBasePath from "src/utils/hooks/useBasePath";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { getYearRange } from "src/utils/Company/Absences/getYearRange";
import getMonthRange from "src/utils/Company/Absences/getMonthRange";
import countDays from "src/utils/Company/Absences/countDays";
import { formatDateRangePreviewFromDates } from "src/utils/Company/Absences/formatDateRangePreview";
import { prepareCustomFieldsDefaultValues } from "src/utils/Company/Absences/prepareAbsenceRequestDefaultFormValues";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import AbsenceRequestDatepicker from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestDatepicker";
import AbsenceHourPicker from "src/Pages/Company/Absences/AbsenceRequest/AbsenceHourPicker";
import BalanceStatusBadge from "src/Pages/Company/Absences/AbsenceRequest/BalanceStatusBadge";
import EmployeesAutocompletePreview from "src/Pages/Company/Absences/AbsenceRequest/EmployeesAutocomplete/EmployeesAutocompletePreview";
import BalanceStatusBadgePreview from "src/Pages/Company/Absences/AbsenceRequest/BalanceStatusBadge/BalanceStatusBadgePreview";
import AbsenceRequestAttachments from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestAttachments";
import AbsenceRequestCustomFields from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestForm/AbsenceRequestCustomFields";
import { IAbsenceRequestFormValues } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import {
  EPatternUnit,
  IAbsenceType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";

interface IAbsenceRequestFormPreviewProps {
  absenceType: IAbsenceType;
}

const AbsenceRequestFormPreview: FC<IAbsenceRequestFormPreviewProps> = ({
  absenceType,
}) => {
  const basePath = useBasePath();
  const history = useHistory();
  const companyId = useCompanyId();
  const logStatus = useLogStatus();

  const initialDate = new Date();

  const currentYear = initialDate.getFullYear().toString();
  const years = getYearRange(currentYear, 5);

  const [displayMonths, setDisplayMonths] = useState(
    getMonthRange(initialDate)
  );
  const [hoursCount, setHoursCount] = useState(0);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "AbsenceRequestFormPreview.tsx",
    });
  }

  if (!absenceType) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Type data provided.",
      file: "AbsenceRequestFormPreview.tsx",
    });
  }

  const formMethods = useForm<IAbsenceRequestFormValues>({
    // resolver: standardSchemaResolver(absenceRequestSchema),
    mode: "onChange",
    // shouldUnregister: false,
    // shouldFocusError: false,
    defaultValues: {
      unit: EPatternUnit.Day,
      dates: [new Date("2026-01-02"), null],
      startTime: 0,
      endTime: 0,
      ...prepareCustomFieldsDefaultValues(
        absenceType.customFieldsConfiguration?.customFields
      ),
    },
  });

  const { control, watch } = formMethods;

  const chosenUnit = watch("unit");
  const chosenDateRange = watch("dates");

  const excludedDays = useMemo(() => {
    const excluded: IExcludedDay[] = [];

    // Add January and February 2026 weekends
    const startDate = new Date(2026, 0, 1); // January 1, 2026
    const endDate = new Date(2026, 1, 28); // February 28, 2026

    for (
      let date = new Date(startDate);
      date <= endDate;
      date.setDate(date.getDate() + 1)
    ) {
      const dayOfWeek = date.getDay();
      // 0 = Sunday, 6 = Saturday
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        excluded.push({
          date: format(new Date(date), "yyyy-MM-dd"),
        });
      }
    }

    // Add January 1st and 6th, 2026 (holidays)
    excluded.push({ date: "2026-01-01" }, { date: "2026-01-06" });

    return excluded;
  }, []);

  const hasValidDateRange = useMemo(
    () => chosenDateRange && chosenDateRange.some((date) => date !== null),
    [chosenDateRange]
  );

  const dayCount = useMemo(() => {
    if (chosenUnit === EPatternUnit.Day) {
      return countDays(
        chosenDateRange,
        absenceType.configurationOptions.billInCalendarDays,
        excludedDays
      );
    }
    if (chosenUnit === EPatternUnit.Hour) {
      if (!hoursCount) return 0;

      return Math.ceil(hoursCount / 8);
    }

    return 0;
  }, [
    absenceType.configurationOptions.billInCalendarDays,
    chosenDateRange,
    chosenUnit,
    excludedDays,
    hoursCount,
  ]);

  useEffect(() => {
    if (chosenUnit === EPatternUnit.Day) {
      let calculatedHoursCount = 0;

      calculatedHoursCount = dayCount * 8;
      setHoursCount(calculatedHoursCount);
    }
  }, [chosenDateRange, chosenUnit, dayCount]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <FormProvider {...formMethods}>
        <form noValidate>
          {absenceType.description && (
            <Fieldset legend={__("Opis")}>
              <Typography variant="body1">{absenceType.description}</Typography>
            </Fieldset>
          )}
          <Fieldset
            legend={__("Termin wniosku")}
            helpText={
              absenceType.configurationOptions.billInHours
                ? __(
                    "Wniosek godzinny można składać wyłącznie na jeden dzień pracy. Jeżeli chcesz złożyć wniosek na kilka dni, wybierz wniosek dzienny."
                  )
                : undefined
            }
          >
            <Controller
              name="unit"
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <FormControl>
                  <RadioGroup
                    value={value}
                    onChange={(event) => {
                      onChange(event.target.value);
                      setHoursCount(0);
                    }}
                    ref={ref}
                    data-testid="absence-request-form-field__unit"
                  >
                    <FormControlLabel
                      value={EPatternUnit.Day}
                      control={<Radio />}
                      label={__("Dzienny")}
                    />
                    {absenceType.configurationOptions.billInHours && (
                      <FormControlLabel
                        value={EPatternUnit.Hour}
                        control={<Radio />}
                        label={__("Godzinowy")}
                      />
                    )}
                  </RadioGroup>
                </FormControl>
              )}
            />
            {absenceType && (
              <Box
                bgcolor="grey.100"
                sx={{
                  padding: "1rem 2rem 1.5rem",
                }}
              >
                <Stack direction="row" spacing={4} alignItems="center">
                  {chosenUnit === EPatternUnit.Day && hasValidDateRange && (
                    <Box>
                      <Typography component="span" variant="subtitle1">
                        {__("Termin")}:{" "}
                      </Typography>
                      <Typography component="span" variant="body1">
                        {formatDateRangePreviewFromDates(chosenDateRange)}
                      </Typography>
                    </Box>
                  )}
                  <BalanceStatusBadge
                    labelUT="Wybrane"
                    days={dayCount}
                    hours={hoursCount}
                    color="primary.dark"
                  />
                </Stack>

                <Divider />

                <BalanceStatusBadgePreview
                  labelUT="Dostępne"
                  color="primary.dark"
                />

                {chosenUnit === EPatternUnit.Day && (
                  <AbsenceRequestDatepicker
                    absenceRequestDays={[]}
                    excludedDays={excludedDays}
                    multipleRequestsPerDayAllowed={
                      absenceType.configurationOptions
                        .multipleRequestsPerDayAllowed
                    }
                    billInCalendarDays={
                      absenceType.configurationOptions.billInCalendarDays
                    }
                    loading={false}
                    handleMonthChange={() => {}}
                    disableMonthChange
                    restrictToMonths={{
                      start: new Date(2026, 0, 1),
                      end: new Date(2026, 1, 28),
                    }}
                  />
                )}
                {chosenUnit === EPatternUnit.Hour && (
                  <AbsenceHourPicker
                    absenceRequestDays={[]}
                    excludedDays={excludedDays}
                    multipleRequestsPerDayAllowed={
                      absenceType.configurationOptions
                        .multipleRequestsPerDayAllowed
                    }
                    billInCalendarDays={
                      absenceType.configurationOptions.billInCalendarDays
                    }
                    hoursCount={hoursCount}
                    setHoursCount={setHoursCount}
                    handleMonthChange={() => {}}
                  />
                )}

                {absenceType.configurationOptions.billInHours && (
                  <Alert severity="info" sx={{ mt: "1rem" }}>
                    {__(
                      "Pierwszy wniosek złożony w danym miesiącu lub roku ustala sposób składania kolejnych. Jeśli wniosek został złożony w trybie dziennym, ta forma będzie obowiązywać dla następnych wniosków w tym okresie."
                    )}
                  </Alert>
                )}
              </Box>
            )}
          </Fieldset>
          <Fieldset legend={__("Uprawnienia i zastępstwo:")}>
            <Typography variant="body2" mb="0.5rem">
              {__("Imię i nazwisko osoby akceptującej wniosek")}:
            </Typography>
            <EmployeesAutocompletePreview
              name="approver"
              label={__("Imię i nazwisko")}
              approvers={absenceType.approvers}
              required={!!absenceType.approvers.length}
              disabled={!absenceType.approvers.length}
            />
            <Typography variant="body2" m="1rem 0 0.5rem">
              {__("Imię i nazwisko osoby wyznaczonej na zastępstwo:")}
            </Typography>
            <EmployeesAutocompletePreview
              name="replacer"
              label={__("Imię i nazwisko")}
              required={absenceType.configurationOptions.replacerRequired}
              disabled={false}
            />
          </Fieldset>
          {absenceType.configurationOptions.justificationRequired && (
            <Fieldset legend={__("Niezbędne informacje:")}>
              <ControlledTextField
                name="justification"
                label={__("Uzasadnienie nieobecności")}
                multiline
                maxRows={3}
                maxLength={255}
                required
                data-testid="absence-request-form-field__justification"
              />
            </Fieldset>
          )}
          {!isEmpty(absenceType.customFieldsConfiguration?.customFields) && (
            <AbsenceRequestCustomFields
              customFields={
                absenceType.customFieldsConfiguration?.customFields || []
              }
            />
          )}
          <Fieldset
            legend={__("Załączniki")}
            required={absenceType.configurationOptions.attachmentRequired}
          >
            <AbsenceRequestAttachments attachments={[]} />
          </Fieldset>
          {absenceType.configurationOptions.commentsAllowed && (
            <Fieldset legend={__("Dodaj komentarz:")} divider={false}>
              <Typography variant="body2" mb="0.5rem">
                {__("Dodawanie komentarza jest opcjonalne.")}
              </Typography>
              <ControlledTextField
                name="comment"
                label={__("Treść komentarza")}
                multiline
                maxRows={3}
                maxLength={255}
                data-testid="absence-request-form-field__comment"
              />
            </Fieldset>
          )}
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            mt="2rem"
          >
            <Button
              variant="outlined"
              size="large"
              onClick={() => history.goBack()}
            >
              {__("Anuluj")}
            </Button>
            <Button
              component={RouterLink}
              variant="contained"
              size="large"
              to={`${basePath}/absences/absence-type/${absenceType.id}/edit`}
              data-testid="absence-request-form__edit-button"
            >
              {__("Edytuj szablon")}
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </LocalizationProvider>
  );
};

export default AbsenceRequestFormPreview;
